import moment from 'moment-timezone';
import { getRuleDisplayName } from '../constants/ruleTypeDefinitions';
import { TRADING_RULES_TZ } from './tradingRulesViolationBuckets';
import { countsTowardViolationStats, isPlanDeviationRuleType } from './violationLimitActual';

/** Calendar days covered by each preset. Matches the period labels on Account Health. */
const RANGE_DAYS = {
  today: 1,
  '7days': 7,
  week: 7,
  '30days': 30,
  month: 30,
  quarter: 90,
  year: 365,
  '2y': 730,
  '5y': 1825,
};

const CANONICAL_RULE_TYPE = {
  trading_window: 'outside_trading_window',
  daily_loss_exceeded: 'max_loss_per_day',
  position_loss_exceeded: 'max_loss_per_position',
  require_stop_loss: 'trade_without_stop_loss',
};

export function canonicalViolationRuleType(ruleType) {
  return CANONICAL_RULE_TYPE[ruleType] || ruleType;
}

export function formatViolationRate(value) {
  if (!Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 10) / 10;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(1);
}

function windowDayCount(timeRange, rows) {
  if (RANGE_DAYS[timeRange]) return RANGE_DAYS[timeRange];

  if (timeRange === 'ytd') {
    const day = moment.tz(TRADING_RULES_TZ).dayOfYear();
    return Math.max(1, day);
  }

  const stamps = rows
    .map((row) => moment(row.violation_timestamp).tz(TRADING_RULES_TZ))
    .filter((m) => m.isValid());
  if (stamps.length === 0) return 1;

  const earliest = moment.min(stamps).clone().startOf('day');
  const today = moment.tz(TRADING_RULES_TZ).startOf('day');
  return Math.max(1, today.diff(earliest, 'days') + 1);
}

function rowIncluded(violation, includePlanDeviations) {
  if (!violation?.rule_type) return false;
  if (countsTowardViolationStats(violation)) return true;
  return includePlanDeviations && isPlanDeviationRuleType(violation.rule_type);
}

function mixObservation(mostCommon, total, typeCount) {
  const name = getRuleDisplayName(mostCommon.type);
  if (typeCount === 1) return `Every violation is ${name}.`;
  const share = mostCommon.count / total;
  if (share >= 0.5) return `${name} is ${mostCommon.count} of ${total}.`;
  return `${name} leads ${typeCount} types (${mostCommon.count} of ${total}).`;
}

function patternObservation({ rows, total, days, timeRange }) {
  const dayKeys = new Set();
  const weekdayCounts = new Map();
  const hourCounts = new Map();
  const stamps = [];

  rows.forEach((row) => {
    const m = moment(row.violation_timestamp).tz(TRADING_RULES_TZ);
    if (!m.isValid()) return;
    stamps.push(m.valueOf());
    dayKeys.add(m.format('YYYY-MM-DD'));
    const weekday = m.format('dddd');
    weekdayCounts.set(weekday, (weekdayCounts.get(weekday) || 0) + 1);
    if (timeRange === 'today') {
      const hour = m.format('h A');
      hourCounts.set(hour, (hourCounts.get(hour) || 0) + 1);
    }
  });

  if (timeRange === 'today') {
    let topHour = null;
    hourCounts.forEach((count, hour) => {
      if (!topHour || count > topHour.count) topHour = { hour, count };
    });
    if (topHour && total >= 2 && topHour.count / total >= 0.6) {
      return `${topHour.count} of ${total} landed around ${topHour.hour}.`;
    }
    if (total >= 2) return 'Spread through the session, not one burst.';
  }

  let topWeekday = null;
  weekdayCounts.forEach((count, day) => {
    if (!topWeekday || count > topWeekday.count) topWeekday = { day, count };
  });
  if (topWeekday && total >= 4 && topWeekday.count / total >= 0.5) {
    return `${topWeekday.day}s account for ${topWeekday.count} of ${total}.`;
  }

  const activeDays = dayKeys.size;
  if (total >= 3 && days >= 7 && activeDays > 0 && activeDays <= Math.max(2, Math.round(days * 0.25))) {
    const noun = activeDays === 1 ? 'day' : 'days';
    return `Bunched onto ${activeDays} ${noun}, not a daily drip.`;
  }

  if (stamps.length >= 4 && days >= 14) {
    stamps.sort((a, b) => a - b);
    const mid = stamps[0] + (stamps[stamps.length - 1] - stamps[0]) / 2;
    const later = stamps.filter((t) => t >= mid).length;
    const share = later / stamps.length;
    if (share >= 0.7) return 'Most of these are in the later part of the range.';
    if (share <= 0.3) return 'Most of these were earlier. The recent pace is quieter.';
  }

  if (activeDays > 1) return `Spread across ${activeDays} days in this range.`;
  if (activeDays === 1) return 'All of them landed on the same day.';
  return 'No second pattern stands out in this range.';
}

/**
 * Period rates plus two short observations for the Violations & Patterns rail.
 * Averages use the selected window (including quiet days). Under a week, the
 * weekly figure is a pace (daily rate × 7), not a completed week.
 */
export function buildViolationPatternStats(violations, timeRange, options = {}) {
  const includePlanDeviations = options.includePlanDeviations === true;
  const rows = (violations || []).filter((row) => rowIncluded(row, includePlanDeviations));
  const total = rows.length;
  const days = windowDayCount(timeRange, rows);
  const avgPerDay = total / days;
  const avgPerWeek = avgPerDay * 7;
  const weekIsPace = days < 7;

  const counts = new Map();
  rows.forEach((row) => {
    const type = canonicalViolationRuleType(row.rule_type);
    counts.set(type, (counts.get(type) || 0) + 1);
  });

  let mostCommon = null;
  counts.forEach((count, type) => {
    if (!mostCommon || count > mostCommon.count) mostCommon = { type, count };
  });

  if (total === 0) {
    return {
      total: 0,
      days,
      avgPerDay: 0,
      avgPerWeek: 0,
      weekIsPace,
      mostCommon: null,
      observations: [
        'No violations in this range.',
        'No repeating pattern to flag.',
      ],
    };
  }

  return {
    total,
    days,
    avgPerDay,
    avgPerWeek,
    weekIsPace,
    mostCommon: {
      ...mostCommon,
      share: mostCommon.count / total,
    },
    observations: [
      mixObservation(mostCommon, total, counts.size),
      patternObservation({ rows, total, days, timeRange }),
    ],
  };
}
